import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSettings } from "./redux/usersSagas";
import { usePopup } from "./hooks/usePopup";
import { useSearch } from "./hooks/useSearch";
import { useVirtualizedRows } from "./hooks/useVirtualizedRows";
import { Add } from "./smartComponents/Add";
import { Edit } from "./smartComponents/Edit";
import { Delete } from "./smartComponents/Delete";
import FilterUsers from "./smartComponents/FilterUsers";
import { Duplicate } from "./smartComponents/Duplicate";
import { Mirror } from "./smartComponents/Mirror";
import { LoginAs } from "./smartComponents/LoginAs";
import { AddAdmin } from "./smartComponents/AddAdmin";
import { Upload } from "./smartComponents/Upload";
import { Reactivate } from "./smartComponents/Reactivate";
import { SetAdmin } from "./smartComponents/SetAdmin";
import { TagsList } from "./smartComponents/Tags";
import { UsersSettingsMenu } from "./smartComponents/UsersSettingsMenu";
import { exportActiveUsers, makeRequest, menuActions } from "./api";
import { UserRow } from "./smartComponents/UserRow";
import { SearchInput } from "../../../components/Input.js/Input";
import LoadingAnimation from "../../../components/loadingAnimation/loadingAnimation";
import "./users.scss";

export function UsersModule({ params }) {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users.users);
  const isLoading = useSelector((state) => state.users.isLoading);
  const areUsersLoading = useSelector((state) => state.users.areUsersLoading);
  const [selectedUser, setSelectedUser] = useState("");
  const [userEmailId, setUserEmailId] = useState(null);
  const { popup, openPopup, closePopup } = usePopup();
  const { searchValue, setSearchValue, filteredUsers } = useSearch(users);
  const { listRef, virtualizedRows, virtualItems, rowVirtualizer } = useVirtualizedRows({
    usersMap: filteredUsers,
    searchValue,
    estimateSize: 40
  });

  useEffect(() => {
    dispatch(fetchSettings(params));
  }, []);

  const onMenuItemClick = ({ value, syntheticEvent }, item = {}) => {
    syntheticEvent.preventDefault();

    let { type, endpoint, requiresUser } = menuActions[value];

    if (type === "request") {
      if (value === "exportActiveUsers") {
        exportActiveUsers();
        return;
      }
      makeRequest(endpoint, item.id);
    }

    if (type === "popup") {
      if (requiresUser) {
        setSelectedUser(item);
      }
      openPopup(value);
    }
  };

  const handlePopup = (item, type) => {
    setSelectedUser(item);

    openPopup(type);
  };

  const handleEmails = (id) => {
    setUserEmailId(id === userEmailId ? null : id);
  };

  if (isLoading)
    return (
      <div className="light">
        <LoadingAnimation uiLoader />
      </div>
    );

  return (
    <div id="admin-users">
      <div className="admin-users">
        <div className="admin-users__menu">
          <UsersSettingsMenu onMenuItemClick={onMenuItemClick} />
          <TagsList />
        </div>

        <div className="admin-users__top">
          <FilterUsers />
        </div>

        <div className="admin-users__content">
          <ul className="group">
            {areUsersLoading ? (
              <LoadingAnimation />
            ) : users && Object.keys(users).length ? (
              <>
                <div className="search">
                  <SearchInput
                    type="text"
                    placeholder="Search..."
                    value={searchValue}
                    onChange={setSearchValue}
                    onClear={() => setSearchValue("")}
                  />
                </div>
                <div ref={listRef}>
                  <div
                    style={{
                      width: "100%",
                      height: `${rowVirtualizer.getTotalSize() + 200}px`,
                      position: "relative"
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        transform: `translateY(${
                          virtualItems[0]
                            ? virtualItems[0].start - rowVirtualizer.options.scrollMargin
                            : 0
                        }px)`,
                        width: "100%"
                      }}
                    >
                      {virtualItems.map(({ index, key }) => {
                        const user = virtualizedRows[index];
                        return (
                          <div key={key} data-index={index} ref={rowVirtualizer.measureElement}>
                            <UserRow
                              user={user}
                              handleEmails={handleEmails}
                              onMenuItemClick={onMenuItemClick}
                              handlePopup={handlePopup}
                              userEmailId={userEmailId}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            ) : null}
            {users && Object.keys(users).length === 0 && (
              <p className="error error--medium">No users found</p>
            )}
          </ul>
        </div>
        {popup.addUser && <Add onClose={closePopup("addUser")} />}
        {popup.addAdminUser && <AddAdmin onClose={closePopup("addAdminUser")} />}
        {popup.bulkUsersCreation && <Upload onClose={closePopup("bulkUsersCreation")} />}

        {popup.edit && (
          <Edit isOpen={popup.edit} onClose={closePopup("edit")} selected={selectedUser} />
        )}

        {popup.setAdmin && (
          <SetAdmin
            isOpen={popup.setAdmin}
            onClose={closePopup("setAdmin")}
            selected={{
              id: selectedUser.id,
              name: selectedUser.name,
              orgName: selectedUser.orgName
            }}
          />
        )}

        {popup.duplicate && (
          <Duplicate
            isOpen={popup.duplicate}
            onClose={closePopup("duplicate")}
            selected={{
              id: selectedUser.id,
              name: selectedUser.name,
              orgName: selectedUser.orgName
            }}
          />
        )}

        {popup.mirror && (
          <Mirror
            isOpen={popup.mirror}
            onClose={closePopup("mirror")}
            selected={{
              id: selectedUser.id,
              name: selectedUser.name,
              organizationId: selectedUser.organizationId,
              orgName: selectedUser.orgName
            }}
          />
        )}

        {popup.delete && (
          <Delete
            isOpen={popup.delete}
            onClose={closePopup("delete")}
            selected={{
              id: selectedUser.id,
              name: selectedUser.name,
              isDeleted: selectedUser.statuses.isDeleted
            }}
          />
        )}

        {popup.reactivate && (
          <Reactivate
            isOpen={popup.reactivate}
            onClose={closePopup("reactivate")}
            selected={{
              id: selectedUser.id,
              name: selectedUser.name
            }}
          />
        )}

        {popup.loginAs && (
          <LoginAs
            isOpen={popup.loginAs}
            onClose={closePopup("loginAs")}
            selected={{ id: selectedUser.id }}
          />
        )}
      </div>
    </div>
  );
}
